import { Card, CardContent } from '@talentmesh/ui/components/card';
import { Progress } from '@talentmesh/ui/components/progress';
import { Spinner } from '@talentmesh/ui/components/spinner';
import { TypographyH2, TypographyMuted } from '@talentmesh/ui/components/typography';
import { cn } from '@talentmesh/ui/lib/utils';
import { Check, Circle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import TMWingsAnimation from '@/Components/Animations/TMWingsAnimation/TMWingsAnimation';
import UIStrings from '@/Utils/UIStrings';

// Estimated durations (seconds) of the backend pipeline; used purely to pace the animation.
// The last step is deliberately generous — we hold on it until the real response arrives.
const STEPS = [
    { title: UIStrings.OneClickStepScrape, duration: 5 },
    { title: UIStrings.OneClickStepExtract, duration: 10 },
    { title: UIStrings.OneClickStepCriteria, duration: 25 },
] as const;

const TOTAL_DURATION = STEPS.reduce((total, { duration }) => total + duration, 0);

const MAX_PROGRESS = 100;

// Time constant for the ease-out fill. Chosen so the bar reaches ~92% around the estimated total
// (1 - e^-2.5), then keeps creeping slowly toward 100% while the server finishes, and holds there.
const TAU_MS = (TOTAL_DURATION * 1000) / 2.5;

// Cap state updates to ~20fps; the bar's CSS transition smooths the gaps, so this stays buttery
// without re-rendering on every animation frame.
const UPDATE_INTERVAL_MS = 50;

const OneClickLoadingAnimation = () => {
    const [elapsedMs, setElapsedMs] = useState(0);

    useEffect(() => {
        let frameId: number;
        let startTs: number | null = null;
        let lastPublishedMs = 0;

        const tick = (timestamp: number) => {
            if (startTs === null) {
                startTs = timestamp;
            }
            const elapsed = timestamp - startTs;
            if (elapsed - lastPublishedMs >= UPDATE_INTERVAL_MS) {
                lastPublishedMs = elapsed;
                setElapsedMs(elapsed);
            }
            frameId = requestAnimationFrame(tick);
        };

        frameId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frameId);
    }, []);

    const elapsedSeconds = elapsedMs / 1000;

    // The step currently in progress. Once we pass the estimated total we stay on the last step
    // (spinner keeps turning) rather than prematurely marking everything complete.
    const activeIndex = useMemo(() => {
        let cumulative = 0;
        for (let index = 0; index < STEPS.length; index += 1) {
            cumulative += STEPS[index].duration;
            if (elapsedSeconds < cumulative) {
                return index;
            }
        }
        return STEPS.length - 1;
    }, [elapsedSeconds]);

    // Asymptotic ease-out: fills fast early, then decelerates and slowly creeps to 100%, holding there.
    const progress = Math.min(MAX_PROGRESS, MAX_PROGRESS * (1 - Math.exp(-elapsedMs / TAU_MS)));
    const isFinalizing = elapsedSeconds >= TOTAL_DURATION;

    return (
        <div className="flex h-full items-center justify-center" data-cy="one-click-loading">
            <Card className="w-full max-w-xl">
                <CardContent className="flex flex-col gap-8 p-8" role="status" aria-live="polite" aria-busy="true">
                    <div className="flex flex-col items-center gap-2 text-center">
                        <TMWingsAnimation width="60px" height="60px" aria-label="creating recruitment animation" />
                        <TypographyH2>{UIStrings.OneClickCreatingTitle}</TypographyH2>
                        <TypographyMuted>
                            {isFinalizing ? UIStrings.OneClickAlmostThere : UIStrings.OneClickCreatingSubtitle}
                        </TypographyMuted>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Progress value={progress} />
                        <span className="text-muted-foreground text-xs">
                            {UIStrings.StepNumberOfTotal(activeIndex + 1, STEPS.length)}
                        </span>
                    </div>

                    <div className="flex flex-col gap-4">
                        {STEPS.map(({ title }, index) => {
                            // Once we pass the estimate ("Almost there…"), every step reads as complete
                            // while the bar keeps creeping to 100%.
                            const isDone = isFinalizing || index < activeIndex;
                            const isActive = !isFinalizing && index === activeIndex;

                            return (
                                <div
                                    key={title}
                                    className={cn(
                                        'flex items-center gap-3 transition-opacity',
                                        !isDone && !isActive && 'opacity-40',
                                    )}
                                >
                                    {isDone ? (
                                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                                            <Check className="h-4 w-4" />
                                        </div>
                                    ) : isActive ? (
                                        <div className="bg-primary/10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full">
                                            <Spinner className="text-primary h-3 w-3" aria-hidden />
                                        </div>
                                    ) : (
                                        <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                                            <Circle className="text-muted-foreground h-3 w-3" />
                                        </div>
                                    )}
                                    <span
                                        className={cn(
                                            'text-sm',
                                            isDone && 'text-muted-foreground',
                                            isActive && 'text-foreground font-medium',
                                        )}
                                    >
                                        {title}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default OneClickLoadingAnimation;
